import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/es.weak-map.js";
import "core-js/modules/web.dom-collections.iterator.js";
/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0 and the Server Side Public License, v 1; you may not use this file except
 * in compliance with, at your election, the Elastic License 2.0 or the Server
 * Side Public License, v 1.
 */

import React, { createContext, useContext, useState, useMemo, useCallback, forwardRef } from 'react';
import { useUpdateEffect } from '../hooks';
import { useEuiTheme } from './hooks';
import { emitEuiProviderWarning } from './warning';

// Typically an object of serialized css`` styles, but can have any amount of nesting, so it's not worth it to try and strictly type this

export var EuiThemeMemoizedStylesContext = /*#__PURE__*/createContext(new WeakMap());
export var EuiThemeMemoizedStylesProvider = ({
  children
}) => {
  // Note: we *should not* use `useMemo` instead of `useState` here, as useMemo is not guaranteed
  // and its cache can get thrown away by React (https://react.dev/reference/react/useMemo#caveats)
  const [componentStyles, rerenderStyles] = useState(new WeakMap());

  // On theme update, wipe the map, which causes the below hook to recompute all styles
  const {
    euiTheme
  } = useEuiTheme();
  useUpdateEffect(() => {
    rerenderStyles(new WeakMap());
  }, [euiTheme]);
  return <EuiThemeMemoizedStylesContext.Provider value={componentStyles}>
      {children}
    </EuiThemeMemoizedStylesContext.Provider>;
};

/**
 * Internal util primarily responsible for getting the memoized styles (if they exist)
 * and if not, generating and setting the styles. DRYed out to facilitate usage
 * between both hook/function components and HOC/class components
 */
const getMemoizedStyles = (stylesGenerator, stylesMap, euiThemeContext) => {
  if (!stylesGenerator.name) {
    emitEuiProviderWarning('Styles are memoized per function. Your style functions must be statically defined in order to not create a new map entry every rerender.');
  }
  const existingStyles = stylesMap.get(stylesGenerator);
  if (existingStyles) {
    return existingStyles;
  } else {
    const generatedStyles = stylesGenerator(euiThemeContext);
    stylesMap.set(stylesGenerator, generatedStyles);
    return generatedStyles;
  }
};

/**
 * Hook that memoizes the returned values of components style fns/generators
 * per-theme
 */
export const useEuiMemoizedStyles = stylesGenerator => {
  const memoizedStyles = useContext(EuiThemeMemoizedStylesContext);
  const euiThemeContext = useEuiTheme();
  const memoizedComponentStyles = useMemo(() => getMemoizedStyles(stylesGenerator, memoizedStyles, euiThemeContext), [stylesGenerator, memoizedStyles, euiThemeContext]);
  return memoizedComponentStyles;
};

/**
 * HOC for class components
 * Syntax is mostly copied from withEuiTheme HOC
 */

export const withEuiStylesMemoizer = Component => {
  const componentName = Component.displayName || Component.name || 'ComponentWithStylesMemoizer';
  const Render = (props, ref) => {
    const memoizedStyles = useContext(EuiThemeMemoizedStylesContext);
    const euiThemeContext = useEuiTheme();
    const stylesMemoizer = useCallback(stylesGenerator => getMemoizedStyles(stylesGenerator, memoizedStyles, euiThemeContext), [memoizedStyles, euiThemeContext]);
    return <Component stylesMemoizer={stylesMemoizer} ref={ref} {...props} />;
  };
  const WithEuiStylesMemoizer = forwardRef(Render);
  WithEuiStylesMemoizer.displayName = componentName;
  return WithEuiStylesMemoizer;
};
try {
  EuiThemeMemoizedStylesProvider.__docgenInfo = {
    tags: {},
    filePath: '/app/src/services/theme/style_memoization.tsx',
    description: '',
    displayName: 'EuiThemeMemoizedStylesProvider',
    methods: [],
    props: {},
    extendedInterfaces: ['Function']
  };
} catch (e) {}
try {
  useEuiMemoizedStyles.__docgenInfo = {
    tags: {},
    filePath: '/app/src/services/theme/style_memoization.tsx',
    description: 'Hook that memoizes the returned values of components style fns/generators\n' + 'per-theme',
    displayName: 'useEuiMemoizedStyles',
    methods: [],
    props: {},
    extendedInterfaces: ['Function']
  };
} catch (e) {}
try {
  withEuiStylesMemoizer.__docgenInfo = {
    tags: {},
    filePath: '/app/src/services/theme/style_memoization.tsx',
    description: '',
    displayName: 'withEuiStylesMemoizer',
    methods: [],
    props: {},
    extendedInterfaces: ['Function']
  };
} catch (e) {}